import { useState, useEffect, useMemo } from 'react';
import {
  probabilityLandsByTurn,
  hypergeometricPMF,
  cardsSeenByTurn,
  getProbabilityColorClass,
} from '../lib/probability';
import { BrewTooltip } from './BrewTooltip';
import { NumericStepper } from './NumericStepper';
import { RotateCcw, Lock, Unlock, CheckCircle2 } from 'lucide-react';

interface LandProbabilityTableProps {
  landCount: number;
  totalCards: number;
  targetDeckSize?: number;
  targetLands?: number;
  onThePlay?: boolean;
  onTogglePlayDraw?: (onPlay: boolean) => void;
  onApplyTargetLands?: (lands: number) => void;
}

type LandCompareMode = 'gte' | 'eq' | 'lte';

export function LandProbabilityTable({
  landCount,
  totalCards,
  targetDeckSize = 60,
  targetLands = 24,
  onThePlay: externalOnThePlay,
  onTogglePlayDraw,
  onApplyTargetLands,
}: LandProbabilityTableProps) {
  const effectiveDeckBase = totalCards >= 40 ? totalCards : targetDeckSize || 60;
  const effectiveLandBase =
    landCount > 0
      ? landCount
      : targetLands || (effectiveDeckBase >= 95 ? 37 : effectiveDeckBase <= 45 ? 17 : 24);

  const [internalPlay, setInternalPlay] = useState(true);
  const onThePlay = externalOnThePlay !== undefined ? externalOnThePlay : internalPlay;
  const setOnThePlay = (val: boolean) => {
    if (onTogglePlayDraw) onTogglePlayDraw(val);
    else setInternalPlay(val);
  };

  // Interactive "What-If" sandbox state
  const [simDeckSize, setSimDeckSize] = useState<number>(effectiveDeckBase);
  const [simLands, setSimLands] = useState<number>(effectiveLandBase);
  const [isCustomSim, setIsCustomSim] = useState(false);

  // Sync with actual deck unless user is actively testing a What-If override
  useEffect(() => {
    if (!isCustomSim) {
      setSimDeckSize(effectiveDeckBase);
      setSimLands(effectiveLandBase);
    }
  }, [effectiveDeckBase, effectiveLandBase, isCustomSim]);

  const [targetTurn, setTargetTurn] = useState<number>(3);
  const [landsNeeded, setLandsNeeded] = useState<number>(3);
  const [compareMode, setCompareMode] = useState<LandCompareMode>('gte');
  const [onCurveLock, setOnCurveLock] = useState<boolean>(true);
  const [startingHand, setStartingHand] = useState<number>(7);
  const [extraDraws, setExtraDraws] = useState<number>(0);
  const [stripMode, setStripMode] = useState<'onCurve' | 'target'>('onCurve');
  const [appliedToast, setAppliedToast] = useState(false);

  const safeDeckSize = Math.max(1, simDeckSize);
  const safeLands = Math.max(0, Math.min(simLands, safeDeckSize));
  const safeHand = Math.max(0, Math.min(startingHand, safeDeckSize));
  const safeExtra = Math.max(0, Math.min(extraDraws, Math.max(0, safeDeckSize - safeHand)));

  // Maximum meaningful turn before decking out (at least 1)
  const maxTurn = Math.max(1, safeDeckSize - safeHand - safeExtra + (onThePlay ? 1 : 0));
  const safeTurn = Math.max(1, Math.min(targetTurn, maxTurn));
  const safeNeeded = Math.max(0, Math.min(landsNeeded, safeLands));

  const handleTurnSelect = (turn: number) => {
    const clampedTurn = Math.max(1, Math.min(turn, maxTurn));
    setTargetTurn(clampedTurn);
    if (onCurveLock) {
      setLandsNeeded(Math.min(clampedTurn, safeLands));
    }
  };

  const handleLandsNeededSelect = (lands: number) => {
    const clamped = Math.max(0, Math.min(lands, safeLands));
    setLandsNeeded(clamped);
    if (clamped !== safeTurn) {
      setOnCurveLock(false);
    }
  };

  const resetToDeck = () => {
    setIsCustomSim(false);
    setSimDeckSize(effectiveDeckBase);
    setSimLands(effectiveLandBase);
    setStartingHand(Math.min(7, effectiveDeckBase));
    setExtraDraws(0);
  };

  // Cards seen by safeTurn (clamped to library size)
  const rawSeen = cardsSeenByTurn(safeTurn, onThePlay, safeExtra, safeHand);
  const cardsSeen = Math.max(0, Math.min(safeDeckSize, rawSeen));

  // Core hypergeometric probabilities
  const probAtLeast =
    safeNeeded <= 0 ? 1 : probabilityLandsByTurn(safeDeckSize, safeLands, safeNeeded, cardsSeen);
  const probExact = hypergeometricPMF(safeDeckSize, safeLands, cardsSeen, safeNeeded);
  const probShort = Math.max(0, 1 - probAtLeast);
  const probAtMost = Math.min(1, probShort + probExact);

  const activeProb =
    compareMode === 'gte' ? probAtLeast : compareMode === 'eq' ? probExact : probAtMost;

  const expectedLandsNum = safeDeckSize > 0 ? cardsSeen * (safeLands / safeDeckSize) : 0;
  const expectedLands = expectedLandsNum.toFixed(1);

  const activePct = Math.round(activeProb * 100);
  const atLeastPct = Math.round(probAtLeast * 100);
  const exactPct = Math.round(probExact * 100);
  const shortPct = Math.round(probShort * 100);

  // Reverse solver: minimum lands K in [0..safeDeckSize] to achieve >=80% and >=90% of >=safeNeeded lands
  const consistencyTargets = useMemo(() => {
    if (safeNeeded <= 0) return { lands80: 0, lands90: 0 };
    if (safeNeeded > cardsSeen) return { lands80: null as number | null, lands90: null as number | null };
    let lands80: number | null = null;
    let lands90: number | null = null;
    for (let k = safeNeeded; k <= safeDeckSize; k++) {
      const p = probabilityLandsByTurn(safeDeckSize, k, safeNeeded, cardsSeen);
      if (lands80 === null && p >= 0.8) lands80 = k;
      if (lands90 === null && p >= 0.9) {
        lands90 = k;
        break;
      }
    }
    return { lands80, lands90 };
  }, [safeDeckSize, safeNeeded, cardsSeen]);

  // Distribution bars for exact land counts drawn in `cardsSeen` cards (9-slot adaptive window)
  const distributionBins = useMemo(() => {
    const maxPossibleDrawn = Math.min(safeLands, cardsSeen);
    const windowSize = 9;
    if (maxPossibleDrawn < windowSize) {
      return Array.from({ length: maxPossibleDrawn + 1 }, (_, k) => ({
        k,
        pmf: hypergeometricPMF(safeDeckSize, safeLands, cardsSeen, k),
      }));
    }
    const center = Math.round(Math.max(0, Math.min(maxPossibleDrawn, safeNeeded || expectedLandsNum)));
    const start = Math.max(0, Math.min(maxPossibleDrawn - windowSize + 1, center - Math.floor(windowSize / 2)));
    return Array.from({ length: windowSize }, (_, idx) => {
      const k = start + idx;
      return {
        k,
        pmf: hypergeometricPMF(safeDeckSize, safeLands, cardsSeen, k),
      };
    });
  }, [safeDeckSize, safeLands, cardsSeen, safeNeeded, expectedLandsNum]);

  const maxBinPmf = Math.max(0.01, ...distributionBins.map((b) => b.pmf));

  // Dynamic 8-turn window so any inspected turn (e.g. Turn 12) stays visible in quick-turn buttons & progression strip
  const turnWindow = useMemo(() => {
    const count = Math.min(8, maxTurn);
    const start = safeTurn <= 8 ? 1 : Math.max(1, Math.min(maxTurn - count + 1, safeTurn - count + 1));
    return Array.from({ length: count }, (_, i) => start + i);
  }, [safeTurn, maxTurn]);

  const deltaFromDeck = safeLands - effectiveLandBase;

  const getPillTheme = (pct: number) => {
    if (pct >= 80)
      return {
        border: 'border-emerald-500/40',
        bg: 'bg-emerald-500/15',
        text: 'text-emerald-300',
        bar: 'bg-emerald-400',
      };
    if (pct >= 65)
      return {
        border: 'border-cyan-500/40',
        bg: 'bg-cyan-500/15',
        text: 'text-cyan-300',
        bar: 'bg-cyan-400',
      };
    if (pct >= 45)
      return {
        border: 'border-amber-500/40',
        bg: 'bg-amber-500/15',
        text: 'text-amber-300',
        bar: 'bg-amber-400',
      };
    return {
      border: 'border-red-500/40',
      bg: 'bg-red-500/15',
      text: 'text-red-300',
      bar: 'bg-red-400',
    };
  };

  const modeSymbol = compareMode === 'gte' ? '≥' : compareMode === 'eq' ? '=' : '≤';

  return (
    <div className="bg-slate-900/70 rounded-xl p-3 border border-amber-500/25 space-y-3">
      {/* Header Row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-amber-400 font-semibold text-xs sm:text-sm">
            Turn-by-Turn Land & On-Curve Calculator
          </span>
          {isCustomSim && (
            <span className="px-1.5 py-0.5 text-[9px] font-mono rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
              What-If Mode {deltaFromDeck !== 0 ? `(${deltaFromDeck > 0 ? `+${deltaFromDeck}` : deltaFromDeck}L)` : ''}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {(isCustomSim || startingHand !== 7 || extraDraws > 0) && (
            <BrewTooltip
              title="Reset to Current Deck"
              description={`Snap calculator back to your deck's actual ${effectiveLandBase} lands, ${effectiveDeckBase} cards, and a standard 7-card opening hand.`}
              accent="slate"
            >
              <button
                type="button"
                onClick={resetToDeck}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
              >
                <RotateCcw className="size-3" />
                <span>Sync Deck</span>
              </button>
            </BrewTooltip>
          )}

          {onApplyTargetLands && safeLands !== targetLands && (
            <BrewTooltip
              title="Apply as Target Land Count"
              badge={`${safeLands} Lands`}
              description={`Updates your BREW Target Land count to ${safeLands} so your ideal curve adjusts to this mana base.`}
              accent="amber"
            >
              <button
                type="button"
                onClick={() => {
                  onApplyTargetLands(safeLands);
                  setAppliedToast(true);
                  setTimeout(() => setAppliedToast(false), 1800);
                }}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 transition-colors"
              >
                {appliedToast ? <CheckCircle2 className="size-3 text-emerald-400" /> : null}
                <span>{appliedToast ? 'Applied!' : `Set Target = ${safeLands}L`}</span>
              </button>
            </BrewTooltip>
          )}
        </div>
      </div>

      {/* Main Headline Result Banner */}
      <div className="bg-gradient-to-r from-purple-950/60 via-slate-900/80 to-amber-950/40 border border-white/10 rounded-lg p-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="space-y-1">
          <div className="text-xs text-white/90 font-medium">
            Chance of drawing{' '}
            <span className="text-amber-300 font-bold">
              {modeSymbol}
              {safeNeeded} land{safeNeeded === 1 ? '' : 's'}
            </span>{' '}
            by <span className="text-cyan-300 font-bold">Turn {safeTurn}</span>{' '}
            <span className="text-white/50 text-[11px]">
              ({onThePlay ? 'On the Play' : 'On the Draw'}, Hand {safeHand}
              {safeExtra > 0 ? `, +${safeExtra} drawn` : ''}, {cardsSeen} cards seen)
            </span>
          </div>

          <div className="flex items-center gap-2.5 text-[10px] font-mono text-white/60 flex-wrap">
            <BrewTooltip
              title="At Least Needed Lands"
              description={`Cumulative probability P(X ≥ ${safeNeeded}) of seeing ${safeNeeded} or more lands in ${cardsSeen} cards.`}
              accent="green"
            >
              <span className="cursor-help">
                ≥{safeNeeded}L: <strong className="text-emerald-300">{atLeastPct}%</strong>
              </span>
            </BrewTooltip>
            <span>•</span>
            <BrewTooltip
              title="Exact Land Count Probability"
              description={`Probability P(X = ${safeNeeded}) of seeing exactly ${safeNeeded} land${safeNeeded === 1 ? '' : 's'} in ${cardsSeen} cards.`}
              accent="cyan"
            >
              <span className="cursor-help">
                ={safeNeeded}L: <strong className="text-white/90">{exactPct}%</strong>
              </span>
            </BrewTooltip>
            <span>•</span>
            <BrewTooltip
              title="Mana Screw Risk (< Needed Lands)"
              description={`Probability P(X < ${safeNeeded}) of falling short (${Math.max(0, safeNeeded - 1)} or fewer lands) by Turn ${safeTurn}.`}
              accent="red"
            >
              <span className="cursor-help">
                Miss (&lt;{safeNeeded}L): <strong className="text-red-300/90">{shortPct}%</strong>
              </span>
            </BrewTooltip>
            <span>•</span>
            <BrewTooltip
              title="Average Lands Drawn"
              description={`Expected (mean) number of lands seen in ${cardsSeen} cards with ${safeLands} lands in a ${safeDeckSize}-card deck.`}
              accent="amber"
            >
              <span className="cursor-help">
                Avg Seen: <strong className="text-amber-300">{expectedLands}L</strong>
              </span>
            </BrewTooltip>

            {consistencyTargets.lands80 !== null && safeNeeded > 0 && (
              <>
                <span>•</span>
                <BrewTooltip
                  title="80% Consistency Benchmark"
                  badge={`${consistencyTargets.lands80} Lands`}
                  description={`To hit ≥${safeNeeded} land${safeNeeded === 1 ? '' : 's'} by Turn ${safeTurn} in at least 80% of games, a ${safeDeckSize}-card deck needs ${consistencyTargets.lands80} lands.`}
                  detail={
                    consistencyTargets.lands90 !== null
                      ? `90% consistency requires ${consistencyTargets.lands90} lands • Click to preview ${consistencyTargets.lands80}L`
                      : `Click to preview ${consistencyTargets.lands80} lands in What-If mode`
                  }
                  accent="purple"
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (consistencyTargets.lands80 !== null) {
                        setIsCustomSim(true);
                        setSimLands(consistencyTargets.lands80);
                      }
                    }}
                    className="px-1.5 py-0.2 rounded bg-purple-500/20 hover:bg-purple-500/35 text-purple-200 border border-purple-400/35 font-semibold transition-colors"
                  >
                    80% Goal: {consistencyTargets.lands80}L
                  </button>
                </BrewTooltip>
              </>
            )}
          </div>
        </div>

        <BrewTooltip
          title={`${modeSymbol}${safeNeeded} Lands by Turn ${safeTurn}`}
          badge={`${activePct}%`}
          description={`Hypergeometric P(X ${modeSymbol} ${safeNeeded}) with Deck N=${safeDeckSize}, Lands K=${safeLands}, Cards Seen n=${cardsSeen}.`}
          detail={
            safeNeeded > cardsSeen
              ? `Impossible: ${safeNeeded} lands needed exceeds ${cardsSeen} total cards seen by Turn ${safeTurn}`
              : activePct >= 80
              ? 'Very reliable consistency threshold'
              : activePct >= 65
              ? 'Solid odds — typical midrange threshold'
              : activePct >= 50
              ? 'Coin-flip territory — consider adding lands, ramp, or card draw'
              : 'Low probability without extra card draw or mana acceleration'
          }
          accent={activePct >= 65 ? 'green' : activePct >= 45 ? 'amber' : 'red'}
        >
          <div className="flex items-baseline gap-1.5 self-end sm:self-center cursor-help bg-black/30 px-3 py-1.5 rounded-lg border border-white/10">
            <span
              className={`text-2xl sm:text-3xl font-black font-mono tracking-tight ${getProbabilityColorClass(
                activeProb
              )}`}
            >
              {activePct}%
            </span>
            <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">
              {compareMode === 'gte' ? 'Hit' : compareMode === 'eq' ? 'Exact' : 'At Most'}
            </span>
          </div>
        </BrewTooltip>
      </div>

      {/* Interactive 2-Column Lab Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        {/* LEFT COLUMN: Deck Size, Lands in Deck, Opening Hand Size (0..Deck), Extra Draws & Play/Draw */}
        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-2.5 flex flex-col justify-between space-y-2.5">
          {/* Land Count Slider + Direct Stepper (0 .. safeDeckSize) */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <BrewTooltip
                title="Simulated Land Count"
                badge={`${safeLands} / ${safeDeckSize} (${((safeLands / safeDeckSize) * 100).toFixed(1)}%)`}
                description="Drag the slider, tap − / + (hold to rapid-step), or type any land count up to your full deck size to test mana ratios."
                accent="amber"
              >
                <span className="text-xs text-white/75 font-medium cursor-help flex items-center gap-1">
                  <span>Lands in Deck:</span>
                  <strong className="text-amber-300 font-mono">{safeLands}</strong>
                  <span className="text-white/40 text-[10px]">
                    ({((safeLands / safeDeckSize) * 100).toFixed(0)}%)
                  </span>
                </span>
              </BrewTooltip>

              <BrewTooltip
                title="Adjust Simulated Lands"
                badge={`0 – ${safeDeckSize}`}
                description="Tap − / + or type an exact land count to simulate."
                accent="amber"
              >
                <NumericStepper
                  value={safeLands}
                  onChange={(val) => {
                    setIsCustomSim(true);
                    setSimLands(val);
                    if (landsNeeded > val) setLandsNeeded(val);
                  }}
                  min={0}
                  max={safeDeckSize}
                  step={1}
                  decimals={0}
                  compact
                  ariaLabel="Lands in Deck"
                  valueClassName="text-amber-300"
                  inputWidthClassName="w-8"
                />
              </BrewTooltip>
            </div>

            <input
              type="range"
              min={0}
              max={safeDeckSize}
              value={safeLands}
              onChange={(e) => {
                const next = parseInt(e.target.value, 10) || 0;
                setIsCustomSim(true);
                setSimLands(next);
                if (landsNeeded > next) setLandsNeeded(next);
              }}
              className="w-full accent-amber-400 h-1.5 bg-white/10 rounded-lg cursor-pointer"
            />
          </div>

          {/* Deck Size + Opening Hand (0..DeckSize) + Extra Draws + Play/Draw */}
          <div className="flex items-center justify-between gap-1.5 pt-2 border-t border-white/10 flex-wrap">
            <BrewTooltip
              title="Simulated Deck Size"
              badge={`${safeDeckSize} Cards`}
              description="Total cards in library (e.g. 60 Constructed, 99 Commander, 40 Limited, or mid-game remaining library)."
              accent="purple"
            >
              <NumericStepper
                label="Deck"
                value={safeDeckSize}
                onChange={(val) => {
                  setIsCustomSim(true);
                  const nextDeck = Math.max(1, val);
                  setSimDeckSize(nextDeck);
                  if (simLands > nextDeck) setSimLands(nextDeck);
                  if (startingHand > nextDeck) setStartingHand(nextDeck);
                }}
                min={1}
                max={250}
                step={1}
                decimals={0}
                compact
                valueClassName="text-white"
                inputWidthClassName="w-9"
              />
            </BrewTooltip>

            <BrewTooltip
              title="Opening Hand / Initial Cards"
              badge={`0 – ${safeDeckSize} Cards`}
              description="Cards in your starting hand (7 normal keep, 6–1 mulligan, or any custom sample size up to your full deck count)."
              detail="Tap − / + (or hold) or type any number directly"
              accent="cyan"
            >
              <NumericStepper
                label="Hand"
                value={safeHand}
                onChange={(val) => setStartingHand(val)}
                min={0}
                max={safeDeckSize}
                step={1}
                decimals={0}
                compact
                valueClassName="text-cyan-300"
                inputWidthClassName="w-8"
              />
            </BrewTooltip>

            <BrewTooltip
              title="Extra Cards Drawn"
              badge={safeExtra > 0 ? `+${safeExtra} Cards` : 'Cantrips / Draw'}
              description="Additional cards seen from cantrips, draw engines, or looting effects beyond normal 1-per-turn draw steps."
              accent="green"
            >
              <NumericStepper
                label="+Draw"
                value={safeExtra}
                onChange={(val) => setExtraDraws(val)}
                min={0}
                max={Math.max(0, safeDeckSize - safeHand)}
                step={1}
                decimals={0}
                compact
                valueClassName="text-emerald-300"
                inputWidthClassName="w-7"
              />
            </BrewTooltip>

            <BrewTooltip
              title="Play vs. Draw"
              badge={onThePlay ? 'On Play' : 'On Draw (+1 card)'}
              description="On the Draw sees 1 additional card every turn (including Turn 1) compared to On the Play."
              accent="purple"
            >
              <div className="inline-flex rounded-lg bg-white/5 border border-white/10 p-0.5">
                <button
                  type="button"
                  onClick={() => setOnThePlay(true)}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                    onThePlay ? 'bg-purple-600 text-white' : 'text-white/50 hover:text-white'
                  }`}
                >
                  Play
                </button>
                <button
                  type="button"
                  onClick={() => setOnThePlay(false)}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                    !onThePlay ? 'bg-amber-600 text-white' : 'text-white/50 hover:text-white'
                  }`}
                >
                  Draw
                </button>
              </div>
            </BrewTooltip>
          </div>
        </div>

        {/* RIGHT COLUMN: Input-Based Turn + Max-Lands-Adjustable Lands Needed + Live Distribution */}
        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-2.5 flex flex-col justify-between space-y-2.5">
          {/* Row 1: Input-Based Turn Stepper + Quick Turn Chips + On-Curve Lock */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <BrewTooltip
                  title="Target Evaluation Turn"
                  badge={`Turn 1 – ${maxTurn}`}
                  description="Type any turn number or tap − / + to inspect early, mid, or late-game land drop odds up to your entire library."
                  accent="cyan"
                >
                  <NumericStepper
                    label="Turn"
                    value={safeTurn}
                    onChange={handleTurnSelect}
                    min={1}
                    max={maxTurn}
                    step={1}
                    decimals={0}
                    compact
                    valueClassName="text-cyan-300"
                    inputWidthClassName="w-8"
                  />
                </BrewTooltip>

                <span className="text-[10px] text-white/45 font-mono">
                  ({cardsSeen}/{safeDeckSize} seen)
                </span>
              </div>

              <BrewTooltip
                title="On-Curve Lock (Turn = Lands)"
                badge={onCurveLock ? 'Locked' : 'Custom'}
                description="When locked, changing Turn T automatically sets Lands Needed = T (capped at total lands in deck). Unlock to test custom combinations like ≥4 lands by Turn 6."
                accent="cyan"
              >
                <button
                  type="button"
                  onClick={() => {
                    const next = !onCurveLock;
                    setOnCurveLock(next);
                    if (next) setLandsNeeded(Math.min(safeTurn, safeLands));
                  }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border transition-colors ${
                    onCurveLock
                      ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                      : 'bg-white/5 text-white/45 border-white/10 hover:text-white'
                  }`}
                >
                  {onCurveLock ? <Lock className="size-2.5" /> : <Unlock className="size-2.5" />}
                  <span>On-Curve Lock</span>
                </button>
              </BrewTooltip>
            </div>

            <div className="grid grid-cols-8 gap-1">
              {turnWindow.map((t) => (
                <BrewTooltip
                  key={t}
                  title={`Inspect Turn ${t}`}
                  badge={`${Math.min(safeDeckSize, cardsSeenByTurn(t, onThePlay, safeExtra, safeHand))} seen`}
                  description={`Set evaluation turn to Turn ${t}${onCurveLock ? ` and check ≥${Math.min(t, safeLands)} land(s)` : ''}.`}
                  accent="cyan"
                >
                  <button
                    type="button"
                    onClick={() => handleTurnSelect(t)}
                    className={`w-full py-0.5 rounded text-[10px] font-mono font-semibold border transition-all ${
                      safeTurn === t
                        ? 'bg-cyan-500/30 text-cyan-200 border-cyan-400/60 shadow-sm'
                        : 'bg-white/5 text-white/50 border-white/10 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    T{t}
                  </button>
                </BrewTooltip>
              ))}
            </div>
          </div>

          {/* Row 2: Lands Needed (0 .. safeLands = Max Lands in Deck) + Compare Mode + Slider & Live Distribution */}
          <div className="pt-2 border-t border-white/10 space-y-1.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <BrewTooltip
                  title="Lands Needed Threshold"
                  badge={`0 – ${safeLands} Lands`}
                  description={`Set any target from 0 up to all ${safeLands} lands in your deck. Tap − / + (or hold), drag the slider, or click any distribution bar below.`}
                  accent="amber"
                >
                  <NumericStepper
                    label="Lands Needed"
                    value={safeNeeded}
                    onChange={handleLandsNeededSelect}
                    min={0}
                    max={safeLands}
                    step={1}
                    decimals={0}
                    compact
                    valueClassName="text-amber-300"
                    inputWidthClassName="w-8"
                  />
                </BrewTooltip>
              </div>

              <div className="inline-flex items-center bg-black/40 border border-white/10 rounded-md p-0.5 text-[10px] font-mono font-bold">
                {(
                  [
                    {
                      id: 'gte',
                      label: '≥ At Least',
                      title: 'At Least K Lands (P ≥ K)',
                      desc: 'Chance of drawing at least this many lands (hit land drop / avoid mana screw).',
                    },
                    {
                      id: 'eq',
                      label: '= Exact',
                      title: 'Exactly K Lands (P = K)',
                      desc: 'Chance of drawing this exact number of lands.',
                    },
                    {
                      id: 'lte',
                      label: '≤ At Most',
                      title: 'At Most K Lands (P ≤ K)',
                      desc: 'Chance of drawing this many lands or fewer (useful for checking screw or flood ceilings).',
                    },
                  ] as const
                ).map((m) => (
                  <BrewTooltip
                    key={m.id}
                    title={m.title}
                    badge={`${m.label.split(' ')[0]} ${safeNeeded}L`}
                    description={m.desc}
                    accent="amber"
                  >
                    <button
                      type="button"
                      onClick={() => setCompareMode(m.id)}
                      className={`px-1.5 py-0.5 rounded transition-colors ${
                        compareMode === m.id
                          ? 'bg-amber-500/30 text-amber-200'
                          : 'text-white/45 hover:text-white'
                      }`}
                    >
                      {m.label}
                    </button>
                  </BrewTooltip>
                ))}
              </div>
            </div>

            <input
              type="range"
              min={0}
              max={Math.max(1, safeLands)}
              disabled={safeLands === 0}
              value={safeNeeded}
              onChange={(e) => handleLandsNeededSelect(parseInt(e.target.value, 10) || 0)}
              className="w-full accent-cyan-400 h-1.5 bg-white/10 rounded-lg cursor-pointer disabled:opacity-30"
            />

            {/* Clickable Exact Land-Count Distribution Mini-Bars for Current Turn */}
            {distributionBins.length > 0 && (
              <div
                className="grid gap-1 pt-0.5"
                style={{ gridTemplateColumns: `repeat(${distributionBins.length}, minmax(0, 1fr))` }}
              >
                {distributionBins.map(({ k, pmf }) => {
                  const pct = Math.round(pmf * 100);
                  const barHeightPct = Math.max(8, Math.round((pmf / maxBinPmf) * 100));
                  const isTarget = k === safeNeeded;
                  const inHighlightedRange =
                    compareMode === 'gte' ? k >= safeNeeded : compareMode === 'eq' ? k === safeNeeded : k <= safeNeeded;

                  return (
                    <BrewTooltip
                      key={k}
                      title={`Exactly ${k} Land${k === 1 ? '' : 's'} by Turn ${safeTurn}`}
                      badge={`${(pmf * 100).toFixed(1)}%`}
                      description={`Click to set Lands Needed = ${k}L. Cumulative ≥${k}L: ${Math.round(
                        (k <= 0 ? 1 : probabilityLandsByTurn(safeDeckSize, safeLands, k, cardsSeen)) * 100
                      )}%.`}
                      accent={isTarget ? 'amber' : inHighlightedRange ? 'cyan' : 'slate'}
                    >
                      <button
                        type="button"
                        onClick={() => handleLandsNeededSelect(k)}
                        className={`w-full rounded border px-0.5 py-1 flex flex-col items-center justify-end transition-all ${
                          isTarget
                            ? 'bg-amber-500/25 border-amber-400/70 ring-1 ring-amber-400/50'
                            : inHighlightedRange
                            ? 'bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20'
                            : 'bg-white/[0.03] border-white/10 hover:bg-white/10 opacity-65 hover:opacity-100'
                        }`}
                      >
                        <div className="w-full h-5 flex items-end justify-center px-1">
                          <div
                            className={`w-full rounded-t transition-all ${
                              isTarget
                                ? 'bg-amber-400'
                                : inHighlightedRange
                                ? 'bg-cyan-400/80'
                                : 'bg-white/25'
                            }`}
                            style={{ height: `${barHeightPct}%` }}
                          />
                        </div>
                        <span className="text-[9px] font-mono font-bold text-white/90 leading-tight mt-0.5">
                          {pct}%
                        </span>
                        <span className="text-[8px] font-mono text-white/50 leading-none">{k}L</span>
                      </button>
                    </BrewTooltip>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Turn-by-Turn Progression Strip (Dynamic Turn Window + On-Curve vs Target Mode) */}
      <div>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">
            {stripMode === 'onCurve'
              ? `On-Curve Land Drop Progression (Turn ${turnWindow[0]} → Turn ${turnWindow[turnWindow.length - 1]})`
              : `Chance of ${modeSymbol}${safeNeeded} Land${safeNeeded === 1 ? '' : 's'} Across Turns (Turn ${turnWindow[0]} → Turn ${turnWindow[turnWindow.length - 1]})`}
          </span>

          <div className="inline-flex items-center bg-black/40 border border-white/10 rounded-md p-0.5 text-[9px] font-semibold">
            <BrewTooltip
              title="On-Curve Progression (≥T Lands on Turn T)"
              description="Shows your chance of hitting every land drop on curve (≥1L on T1, ≥2L on T2, ≥3L on T3, etc.)."
              accent="cyan"
            >
              <button
                type="button"
                onClick={() => setStripMode('onCurve')}
                className={`px-2 py-0.5 rounded transition-colors ${
                  stripMode === 'onCurve' ? 'bg-cyan-500/30 text-cyan-200' : 'text-white/45 hover:text-white'
                }`}
              >
                On-Curve (≥TL)
              </button>
            </BrewTooltip>
            <BrewTooltip
              title={`Track ${modeSymbol}${safeNeeded} Lands by Turn`}
              description={`Shows how your probability of hitting ${modeSymbol}${safeNeeded} land${safeNeeded === 1 ? '' : 's'} climbs turn by turn.`}
              accent="amber"
            >
              <button
                type="button"
                onClick={() => setStripMode('target')}
                className={`px-2 py-0.5 rounded transition-colors ${
                  stripMode === 'target' ? 'bg-amber-500/30 text-amber-200' : 'text-white/45 hover:text-white'
                }`}
              >
                Track {modeSymbol}
                {safeNeeded}L
              </button>
            </BrewTooltip>
          </div>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
          {turnWindow.map((t) => {
            const seenT = Math.min(safeDeckSize, cardsSeenByTurn(t, onThePlay, safeExtra, safeHand));
            const reqLands = stripMode === 'onCurve' ? t : safeNeeded;
            const probT =
              stripMode === 'onCurve'
                ? probabilityLandsByTurn(safeDeckSize, safeLands, t, seenT)
                : compareMode === 'gte'
                ? reqLands <= 0
                  ? 1
                  : probabilityLandsByTurn(safeDeckSize, safeLands, reqLands, seenT)
                : compareMode === 'eq'
                ? hypergeometricPMF(safeDeckSize, safeLands, seenT, reqLands)
                : Math.min(
                    1,
                    Math.max(0, 1 - (reqLands <= 0 ? 1 : probabilityLandsByTurn(safeDeckSize, safeLands, reqLands, seenT))) +
                      hypergeometricPMF(safeDeckSize, safeLands, seenT, reqLands)
                  );

            const pctT = Math.round(probT * 100);
            const theme = getPillTheme(pctT);
            const isSelected =
              stripMode === 'onCurve' ? safeTurn === t && safeNeeded === Math.min(t, safeLands) : safeTurn === t;
            const tagSymbol = stripMode === 'onCurve' ? '≥' : modeSymbol;

            return (
              <BrewTooltip
                key={t}
                title={`Turn ${t} (${tagSymbol}${reqLands} Land${reqLands === 1 ? '' : 's'})`}
                badge={`${pctT}%`}
                description={`${pctT}% chance of seeing ${tagSymbol}${reqLands} land${reqLands === 1 ? '' : 's'} in your first ${seenT} cards (${onThePlay ? 'Play' : 'Draw'}).`}
                detail={`Avg lands seen by T${t}: ${(safeDeckSize > 0 ? seenT * (safeLands / safeDeckSize) : 0).toFixed(1)}L`}
                accent={pctT >= 65 ? 'green' : pctT >= 45 ? 'amber' : 'red'}
              >
                <button
                  type="button"
                  onClick={() => {
                    setTargetTurn(t);
                    if (stripMode === 'onCurve') {
                      setLandsNeeded(Math.min(t, safeLands));
                      setCompareMode('gte');
                      setOnCurveLock(true);
                    }
                  }}
                  className={`w-full flex flex-col items-center p-1.5 rounded-lg border transition-all ${theme.bg} ${theme.border} ${
                    isSelected ? 'ring-2 ring-amber-400 scale-[1.03]' : 'hover:brightness-125'
                  }`}
                >
                  <div className="flex items-center justify-between w-full text-[9px] text-white/60 font-mono">
                    <span>T{t}</span>
                    <span>
                      {tagSymbol}
                      {reqLands}L
                    </span>
                  </div>
                  <div className={`text-sm font-black font-mono my-0.5 ${theme.text}`}>{pctT}%</div>
                  <div className="w-full h-1 bg-black/40 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${theme.bar}`}
                      style={{ width: `${Math.min(100, Math.max(4, pctT))}%` }}
                    />
                  </div>
                </button>
              </BrewTooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
}
